import React, { Fragment, useState, useEffect } from "react";
import Topbar from "../layouts/TopBar";
import ChecklistResources from "../resources/ChecklistResources";
import toast from 'react-hot-toast';
import { connect } from "react-redux";
import { loginSuccess, logout } from "../redux/actions/LoggedInAction";
import {ACCESS_DENIED} from '../Messages'

function ManageProdChecklist({isLoggedIn, loggedInUser}){

    const [checklistItems, setChecklistItems] = useState([])
    const [categories, setCategories] = useState([])
    const [newItemName, setNewItemName] = useState('')
    const [newItemCategory, setNewItemCategory] = useState('')
    const [editingItem, setEditingItem] = useState(null)
    const [editName, setEditName] = useState('')
    const [editCategory, setEditCategory] = useState('')
    const [activeCategory, setActiveCategory] = useState(null)

    const resources = new ChecklistResources()

    const loadItems = async () => {
        const response = await resources.get_admin()
        if (response.error && response.error.length > 0) {
            toast.error("Failed to load checklist items: " + response.error)
        } else {
            const items = response['body'] || []
            setChecklistItems(items)
            const uniqueCats = [...new Set(items.map(i => i.item_classification).filter(Boolean))]
            setCategories(uniqueCats)
            if (!activeCategory && uniqueCats.length > 0) setActiveCategory(uniqueCats[0])
        }
    }

    useEffect(() => { loadItems().catch(err => toast.error("Network error: " + err)) }, [])

    // Items for the active category, sorted by sort_order
    const activeItems = checklistItems
        .filter(i => i.item_classification === activeCategory)
        .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))

    const saveOrder = (reorderedItems) => {
        const payload = reorderedItems.map((item, idx) => ({ id: item.id, sort_order: idx }))
        resources.admin_reorder(payload).catch(() => {})
        // Optimistically update local state
        setChecklistItems(prev => {
            const updated = [...prev]
            reorderedItems.forEach((item, idx) => {
                const found = updated.find(i => i.id === item.id)
                if (found) found.sort_order = idx
            })
            return updated
        })
    }

    const moveItem = (itemId, direction) => {
        const items = [...activeItems]
        const idx = items.findIndex(i => i.id === itemId)
        const swapIdx = idx + direction
        if (swapIdx < 0 || swapIdx >= items.length) return
        ;[items[idx], items[swapIdx]] = [items[swapIdx], items[idx]]
        saveOrder(items)
    }

    const handleAddItem = () => {
        if (!newItemName.trim()) { toast.error("Please enter an item name"); return }
        if (!newItemCategory.trim()) { toast.error("Please enter a category"); return }
        const isNew = !categories.includes(newItemCategory.trim())
        if (isNew && !window.confirm(`"${newItemCategory.trim()}" is a new category. Continue?`)) return
        if (!window.confirm("Add this checklist item?")) return

        resources.admin_post({ name: newItemName.trim(), item_classification: newItemCategory.trim() }).then(res => {
            if (res.error && res.error.length > 0) { toast.error("Failed: " + res.error); return }
            setNewItemName('')
            setNewItemCategory('')
            loadItems()
        }).catch(err => toast.error("Network error: " + err))
    }

    const handleSaveEdit = (itemId) => {
        if (!editName.trim()) { toast.error("Please enter an item name"); return }
        if (!editCategory.trim()) { toast.error("Please enter a category"); return }
        const isNew = !categories.includes(editCategory.trim())
        if (isNew && !window.confirm(`"${editCategory.trim()}" is a new category. Continue?`)) return
        if (!window.confirm("Update this item?")) return

        resources.admin_put(itemId, { name: editName.trim(), item_classification: editCategory.trim() }).then(res => {
            if (res.error && res.error.length > 0) { toast.error("Failed: " + res.error); return }
            setEditingItem(null)
            loadItems()
        }).catch(err => toast.error("Network error: " + err))
    }

    const handleDeleteItem = (item) => {
        if (!window.confirm(`Delete "${item.name}"?`)) return
        resources.admin_delete(item.id).then(res => {
            if (res.error && res.error.length > 0) { toast.error("Failed: " + res.error); return }
            loadItems()
        }).catch(err => toast.error("Network error: " + err))
    }

    const btnStyle = (active) => ({
        padding: '7px 16px',
        borderRadius: '20px',
        border: active ? '2px solid #003165' : '2px solid #e0e0e0',
        backgroundColor: active ? '#003165' : '#fff',
        color: active ? '#ffa800' : '#555',
        fontWeight: 'bold',
        fontSize: '13px',
        cursor: 'pointer',
        whiteSpace: 'nowrap'
    })

    return(
        <Fragment>
            <Topbar/>

            <div className="hero-area">
                <div className="page-banner parallax" style={{backgroundColor:"#003165"}}>
                    <div className="container">
                        <div className="page-banner-text">
                            {(isLoggedIn && loggedInUser['role.name'] === 'admin') ?
                                <h1 style={{color:"#ffa800"}} className="block-title"><strong>Manage Production Checklist</strong></h1>
                                :
                                <h1 style={{color:"#ffa800"}} className="block-title"><strong>{ACCESS_DENIED}</strong></h1>
                            }
                        </div>
                    </div>
                </div>
            </div>

            <div id="main-container">
                {(isLoggedIn && loggedInUser['role.name'] === 'admin') ? (
                <div className="content">
                    <div className="container">
                        <div className="spacer-40"></div>

                        {/* Add New Item */}
                        <div style={{backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e0e0e0', padding: '20px', marginBottom: '24px'}}>
                            <h3 style={{color: '#003165', margin: '0 0 15px', fontSize: 'clamp(15px, 4vw, 17px)'}}>Add New Item</h3>
                            <div style={{display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'flex-end'}}>
                                <div style={{flex: '1 1 250px'}}>
                                    <label style={{color: '#003165', fontWeight: 'bold', fontSize: '13px', marginBottom: '4px', display: 'block'}}>Item Name</label>
                                    <input type="text" value={newItemName} onChange={e => setNewItemName(e.target.value)}
                                        placeholder="Enter item name"
                                        style={{width: '100%', padding: '8px 10px', border: '1px solid #ccc', borderRadius: '4px', boxSizing: 'border-box', fontSize: '13px'}} />
                                </div>
                                <div style={{flex: '1 1 200px'}}>
                                    <label style={{color: '#003165', fontWeight: 'bold', fontSize: '13px', marginBottom: '4px', display: 'block'}}>Category</label>
                                    <input type="text" value={newItemCategory} onChange={e => setNewItemCategory(e.target.value)}
                                        placeholder="Enter or select category" list="categories-list"
                                        style={{width: '100%', padding: '8px 10px', border: '1px solid #ccc', borderRadius: '4px', boxSizing: 'border-box', fontSize: '13px'}} />
                                    <datalist id="categories-list">
                                        {categories.map((cat, idx) => <option key={idx} value={cat} />)}
                                    </datalist>
                                </div>
                                <button onClick={handleAddItem}
                                    style={{backgroundColor: '#35ab7c', color: '#fff', border: 'none', borderRadius: '4px', padding: '9px 22px', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px'}}>
                                    Add Item
                                </button>
                            </div>
                        </div>

                        {/* Category tabs */}
                        <div style={{display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px'}}>
                            {categories.map(cat => (
                                <button key={cat} onClick={() => { setActiveCategory(cat); setEditingItem(null) }}
                                    style={btnStyle(activeCategory === cat)}>
                                    {cat}
                                </button>
                            ))}
                        </div>

                        {/* Items list for active category */}
                        {activeCategory && (
                            <div style={{backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e0e0e0', overflow: 'hidden'}}>
                                <div style={{backgroundColor: '#003165', padding: '12px 20px'}}>
                                    <span style={{color: '#ffa800', fontWeight: 'bold', fontSize: '15px'}}>{activeCategory}</span>
                                    <span style={{color: '#aac0d8', fontSize: '12px', marginLeft: '10px'}}>{activeItems.length} items — use arrows to reorder</span>
                                </div>
                                {activeItems.length === 0 && (
                                    <div style={{padding: '30px', textAlign: 'center', color: '#999', fontSize: '14px'}}>No items in this category.</div>
                                )}
                                {activeItems.map((item, idx) => (
                                    <div key={item.id} style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '10px',
                                        padding: '10px 16px',
                                        borderBottom: idx < activeItems.length - 1 ? '1px solid #f0f0f0' : 'none',
                                        backgroundColor: idx % 2 === 0 ? '#fff' : '#fafafa'
                                    }}>
                                        {/* Order arrows */}
                                        <div style={{display: 'flex', flexDirection: 'column', gap: '2px', flexShrink: 0}}>
                                            <button onClick={() => moveItem(item.id, -1)} disabled={idx === 0}
                                                style={{background: 'none', border: '1px solid #ccc', borderRadius: '3px', width: '24px', height: '22px', cursor: idx === 0 ? 'not-allowed' : 'pointer', opacity: idx === 0 ? 0.3 : 1, fontSize: '11px', padding: 0}}>
                                                ▲
                                            </button>
                                            <button onClick={() => moveItem(item.id, 1)} disabled={idx === activeItems.length - 1}
                                                style={{background: 'none', border: '1px solid #ccc', borderRadius: '3px', width: '24px', height: '22px', cursor: idx === activeItems.length - 1 ? 'not-allowed' : 'pointer', opacity: idx === activeItems.length - 1 ? 0.3 : 1, fontSize: '11px', padding: 0}}>
                                                ▼
                                            </button>
                                        </div>

                                        {/* Item content */}
                                        <div style={{flex: 1, minWidth: 0}}>
                                            {editingItem === item.id ? (
                                                <div style={{display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center'}}>
                                                    <input type="text" value={editName} onChange={e => setEditName(e.target.value)}
                                                        style={{flex: '1 1 200px', padding: '6px 8px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '13px'}} />
                                                    <input type="text" value={editCategory} onChange={e => setEditCategory(e.target.value)}
                                                        list="edit-categories-list"
                                                        style={{flex: '0 1 160px', padding: '6px 8px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '13px'}} />
                                                    <datalist id="edit-categories-list">
                                                        {categories.map((cat, i) => <option key={i} value={cat} />)}
                                                    </datalist>
                                                </div>
                                            ) : (
                                                <span style={{fontSize: '13px', color: '#333'}}>{item.name}</span>
                                            )}
                                        </div>

                                        {/* Action buttons */}
                                        <div style={{display: 'flex', gap: '6px', flexShrink: 0}}>
                                            {editingItem === item.id ? (
                                                <>
                                                    <button onClick={() => handleSaveEdit(item.id)}
                                                        style={{backgroundColor: '#35ab7c', color: '#fff', border: 'none', borderRadius: '4px', padding: '5px 12px', cursor: 'pointer', fontSize: '12px'}}>
                                                        Save
                                                    </button>
                                                    <button onClick={() => setEditingItem(null)}
                                                        style={{backgroundColor: '#6c757d', color: '#fff', border: 'none', borderRadius: '4px', padding: '5px 12px', cursor: 'pointer', fontSize: '12px'}}>
                                                        Cancel
                                                    </button>
                                                </>
                                            ) : (
                                                <>
                                                    <button onClick={() => { setEditingItem(item.id); setEditName(item.name); setEditCategory(item.item_classification || '') }}
                                                        style={{backgroundColor: '#ffa800', color: '#fff', border: 'none', borderRadius: '4px', padding: '5px 12px', cursor: 'pointer', fontSize: '12px'}}>
                                                        Edit
                                                    </button>
                                                    <button onClick={() => handleDeleteItem(item)}
                                                        style={{backgroundColor: '#d80000', color: '#fff', border: 'none', borderRadius: '4px', padding: '5px 12px', cursor: 'pointer', fontSize: '12px'}}>
                                                        Delete
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="spacer-50"></div>
                    </div>
                </div>
                ) : <></>}
            </div>
        </Fragment>
    )
}

const mapStateToProps = (state) => {
    return{
       isLoggedIn: state.authReducer.isLoggedIn,
       loggedInUser: state.authReducer.user
    };
};

const mapDispatchToProps = {
    logout
};

export default connect(mapStateToProps, mapDispatchToProps)(ManageProdChecklist);
